import React, { useEffect, useState } from 'react';
import { LayoutDashboard, FileText, TrendingUp, BarChart3, Zap, Activity, LineChart, Map, Settings, ChevronLeft, TrendingDown, Grid2x2, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
import { ViewType } from '../hooks/useDashboardState';
import { Dashboard } from './views/DashboardsView';
interface SidebarProps {
  collapsed: boolean;
  toggleCollapsed: () => void;
  activeView: ViewType;
  setActiveView: (view: ViewType) => void;
}
const navItems: {
  icon: any;
  label: string;
  view: ViewType;
  hasSubNav?: boolean;
}[] = [{
  icon: LayoutDashboard,
  label: 'Overview',
  view: 'overview'
}, {
  icon: FileText,
  label: 'Briefing',
  view: 'briefing'
}, {
  icon: Zap,
  label: 'Oracle',
  view: 'oracle'
}, {
  icon: TrendingUp,
  label: 'Forecast',
  view: 'forecast'
}, {
  icon: BarChart3,
  label: 'Benchmarks',
  view: 'benchmarks'
}, {
  icon: Activity,
  label: 'Live Benchmarking',
  view: 'live-benchmarking'
}, {
  icon: LineChart,
  label: 'Energy Market Aggregates',
  view: 'market-aggregates'
}, {
  icon: TrendingDown,
  label: 'Trading Strategies',
  view: 'trading-strategies'
}, {
  icon: Grid2x2,
  label: 'Dashboards',
  view: 'dashboards',
  hasSubNav: true
}, {
  icon: Map,
  label: 'Map',
  view: 'map'
}, {
  icon: Settings,
  label: 'Settings',
  view: 'settings'
}];
export function Sidebar({
  collapsed,
  toggleCollapsed,
  activeView,
  setActiveView
}: SidebarProps) {
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [dashboardsExpanded, setDashboardsExpanded] = useState(true);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  // Load dashboards
  useEffect(() => {
    const loadDashboards = () => {
      const saved = localStorage.getItem('jua-dashboards-v2');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setDashboards(parsed);
        } catch (e) {
          console.error('Failed to load dashboards', e);
        }
      }
    };
    loadDashboards();
    // Listen for dashboard updates
    const handleUpdate = () => loadDashboards();
    window.addEventListener('dashboard-update', handleUpdate);
    window.addEventListener('storage', handleUpdate);
    return () => {
      window.removeEventListener('dashboard-update', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);
  // Auto-expand dashboards section when a dashboard is selected
  useEffect(() => {
    if (activeView === 'dashboards' && dashboards.length > 0) {
      setDashboardsExpanded(true);
    }
  }, [activeView, dashboards.length]);
  // Auto-collapse on mobile/tablet screens with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const isMobile = window.innerWidth < 1024;
        if (isMobile && !collapsed) {
          toggleCollapsed();
        }
      }, 150);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [collapsed, toggleCollapsed]);
  const handleDashboardClick = (dashboardId: string) => {
    setSelectedDashboardId(dashboardId);
    setActiveView('dashboards');
    // Dispatch event with dashboard ID
    window.dispatchEvent(new CustomEvent('select-dashboard', {
      detail: {
        dashboardId
      }
    }));
  };
  return <motion.aside initial={false} animate={{
    width: collapsed ? 80 : 260
  }} className="fixed left-0 top-0 h-screen bg-white/60 backdrop-blur-xl border-r border-slate-50 z-50 flex flex-col shadow-xl shadow-slate-200/40" data-id="element-591">
      {/* Logo Area */}
      <div className="h-16 flex items-center px-6 border-b border-slate-200/50" data-id="element-592">
        {collapsed ? <div className="w-8 h-8 flex items-center justify-center" data-id="element-593">
            <span className="text-xl font-bold text-slate-900" data-id="element-594">J</span>
          </div> : <Logo size="md" data-id="element-595" />}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto custom-scrollbar" data-id="element-596">
        {navItems.map(item => {
        const isActive = activeView === item.view;
        const isDashboardsItem = item.view === 'dashboards';
        return <div key={item.view} data-id="element-597">
              <button onClick={() => {
            if (isDashboardsItem && !collapsed) {
              setDashboardsExpanded(!dashboardsExpanded);
            }
            setActiveView(item.view);
            if (isDashboardsItem && dashboards.length > 0) {
              setSelectedDashboardId(dashboards[0].id);
              window.dispatchEvent(new CustomEvent('select-dashboard', {
                detail: {
                  dashboardId: dashboards[0].id
                }
              }));
            }
          }} className={`w-full flex items-center gap-3 py-2.5 rounded-lg transition-all duration-200 group relative overflow-hidden ${isActive ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-pink-500/10 ring-1 ring-white/60 pl-4 pr-3' : 'text-slate-600 hover:text-slate-900 hover:bg-white/40 hover:backdrop-blur-sm px-3'}`} data-id="element-598">
                {isActive && <>
                    <motion.div layoutId="activeNavGlow" className="absolute inset-0 bg-gradient-to-br from-white/60 via-pink-50/40 to-purple-50/30" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-599" />
                    <motion.div layoutId="activeNav" className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-pink-500 via-pink-400 to-purple-500 rounded-r-full shadow-sm shadow-pink-500/50" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-600" />
                  </>}

                <item.icon size={20} className={`relative z-10 transition-colors ${isActive ? 'text-pink-600' : 'text-slate-600 group-hover:text-slate-900'}`} data-id="element-601" />
                {!collapsed && <>
                    <span className={`font-medium text-sm truncate relative z-10 flex-1 text-left ${isActive ? 'text-pink-600' : ''}`} data-id="element-602">
                      {item.label}
                    </span>
                    {isDashboardsItem && dashboards.length > 0 && <motion.div animate={{
                rotate: dashboardsExpanded ? 0 : -90
              }} transition={{
                duration: 0.2
              }} className="relative z-10" data-id="element-603">
                        <ChevronDown size={16} className={isActive ? 'text-pink-600' : 'text-slate-400'} data-id="element-604" />
                      </motion.div>}
                  </>}
              </button>

              {/* Dashboard Sub-navigation */}
              {isDashboardsItem && !collapsed && <AnimatePresence data-id="element-605">
                  {dashboardsExpanded && dashboards.length > 0 && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-hidden" data-id="element-606">
                      <div className="ml-7 mt-1 space-y-0.5 border-l border-slate-200 pl-3" data-id="element-607">
                        {dashboards.map(dashboard => <button key={dashboard.id} onClick={() => handleDashboardClick(dashboard.id)} className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedDashboardId === dashboard.id ? 'bg-pink-50 text-pink-700' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} data-id="element-608">
                            <div className="truncate" data-id="element-609">{dashboard.title}</div>
                          </button>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>}
            </div>;
      })}
      </nav>

      {/* Footer Toggle */}
      <div className="p-4 border-t border-slate-200/50" data-id="element-610">
        <button onClick={toggleCollapsed} className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-slate-100/80 text-slate-600 hover:text-slate-900 transition-colors" data-id="element-611">
          <motion.div animate={{
          rotate: collapsed ? 180 : 0
        }} transition={{
          duration: 0.2
        }} data-id="element-612">
            <ChevronLeft size={20} data-id="element-613" />
          </motion.div>
        </button>
      </div>
    </motion.aside>;
}